import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { AADocument } from '@55degrees/aa-core-ts-data-formats';
import { Generator } from '@55degrees/aa-data-examples';
import { reportMixPanel } from '@55degrees/consent-tracker';
import { Chart } from '../types/A3';
import { ProductFruitsReporter } from '../components/Helpers/ProductFruitsInit';
import { DataLoaderData, DataLoaderStartOptions, DatasetInfoProperties } from '../types';
import { A3DocumentContextType, A3DocumentProviderProps } from './types';
import { createDatasetInfoProperties, getSourceNameFromLoadMode } from './helper';
import DataLoaderContext from './DataLoaderContext';
import useSavedDatasets from '../hooks/useSavedDatasets';

export const A3DocumentContext = createContext<A3DocumentContextType>({} as A3DocumentContextType);

function A3DocumentContextProvider({
  a3Document,
  children,
  closeDataLoader,
  setA3Document,
  setIsFirstLoad,
  updateLoadingCounter,
  updateLastLoadedData,
  dataLoaderStartOptions,
}: A3DocumentProviderProps) {
  const navigate = useNavigate();
  const { favoriteDatasets } = useSavedDatasets();
  const { trigger } = useContext(DataLoaderContext);

  const [activeChart, setActiveChart] = useState<Chart>('dashboard');
  const [datasetInfoProperties, setDatasetInfoProperties] = useState<DatasetInfoProperties>({
    data: [],
    isHidden: true,
  });

  const loadState = (dataLoaderStartOptions as DataLoaderStartOptions)?.loadState;

  const loadA3Document = (aaDocument: AADocument) => {
    setActiveChart('dashboard');
    updateLoadingCounter();
    setA3Document(aaDocument);
    setIsFirstLoad(false);
  };

  const loadA3FromDataLoader = (dataLoaderData: DataLoaderData) => {
    const { data: aaDocument, uuid = '', ...datasetInfo } = dataLoaderData;
    loadA3Document(aaDocument);
    updateLastLoadedData(uuid);
    const newDatasetInfoProperties = createDatasetInfoProperties(false, datasetInfo);
    setDatasetInfoProperties(newDatasetInfoProperties);
    const eventTrigger = loadState === 'edit' ? 'Edit' : trigger;

    const isDatasetFavorited = favoriteDatasets.find((item) => {
      if (item.name === datasetInfo.name) return item.favorite;
    });

    reportMixPanel('Data Load Finish', {
      Trigger: eventTrigger,
      Type: isDatasetFavorited ? 'Favorite' : 'Non-Favorite',
      'New Statuses': 'No',
      Shared: 'No',
      'Issue Count': dataLoaderData.data.items.length,
      Views: 0,
      'Loading Time': dataLoaderData.loadingTime,
      DataSource: getSourceNameFromLoadMode(dataLoaderData.loadMode),
    });

    navigate(`/${uuid}`);
    closeDataLoader();
  };

  const loadA3ExampleData = () => {
    const exampleA3Document = Generator('Default');
    loadA3Document(exampleA3Document);
    const newDatasetInfoProperties = createDatasetInfoProperties(true);
    setDatasetInfoProperties(newDatasetInfoProperties);
    ProductFruitsReporter.track('loadExampleData');
    reportMixPanel('Data Load Finish', {
      Trigger: 'Dropdown',
      Type: 'Example',
      'New Statuses': 'No',
      Shared: 'No',
      Views: 0,
    });
    navigate('/example');
  };

  const loadA3FromFileLoader = (aaDocument: AADocument) => {
    loadA3Document(aaDocument);
    const newDatasetInfoProperties = createDatasetInfoProperties(true);
    setDatasetInfoProperties(newDatasetInfoProperties);
    ProductFruitsReporter.track('loadRealData');
    reportMixPanel('Data Load Finish', {
      Trigger: 'Dropdown',
      Type: 'External',
      'New Statuses': 'No',
      Shared: 'No',
      Views: 0,
      'Issue Count': aaDocument?.items?.length || 'Unknown',
    });
    navigate('/external');
  };

  const a3DocumentTitle = a3Document?.title ?? '';

  const values = {
    a3Document: a3Document,
    a3DocumentTitle: a3DocumentTitle,
    activeChart: activeChart,
    datasetInfoProperties: datasetInfoProperties,
    loadA3ExampleData: loadA3ExampleData,
    loadA3FromDataLoader: loadA3FromDataLoader,
    loadA3FromFileLoader: loadA3FromFileLoader,
    setA3Document: setA3Document,
    setActiveChart: setActiveChart,
  };

  return <A3DocumentContext.Provider value={values}>{children}</A3DocumentContext.Provider>;
}

export default A3DocumentContextProvider;
